import { Questions } from './../models/questions';
import { environment } from '../../environments/environment';

import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Question } from '../models/question';
import { Student } from '../models/student';

import { EasyDebugDecorator } from '../../app/decorators/easy-debug.decorator';

@Injectable({
  providedIn: 'root',
})
@EasyDebugDecorator
export class QuestionsService extends Questions {
  private _questionsToSync: Array<Question> = [];

  constructor(private http: HttpClient) {
    super();
  }

  // --- Inlined from RemoteQuestions ---

  async getQuestion(questionId: string, student: Student, local?: boolean): Promise<any> {
    if (!!this.questions && this.questions.length > 0 && this.questions.find((elt) => elt.id === questionId)) {
      return this.questions.find((elt) => elt.id === questionId);
    }
    const url = `${environment.cdrBase}/v${environment.apiVersion}/questions/${questionId}`;

    return this.http
      .get(url)
      .pipe(
        switchMap((questionData: any) => {
          if (!!questionData && !!questionData.data) {
            const question = this.fillQuestion(questionData.data);
            this.questions.push(question);
            return of(question);
          }
          return of({
            errorCode: 'E301',
            errorMessage: 'remote getQuestion Service failed',
            errorOriginal: 'No questionsData or no data in questionsData',
          });
        }),
        catchError((err) => {
          return of({
            errorCode: 'E301',
            errorMessage: 'remote getQuestion Service failed',
            errorOriginal: err,
          });
        })
      )
      .toPromise();
  }

  async getQuestions(student: Student, local?: boolean): Promise<any> {
    if (!!this.questions && this.questions.length > 0) {
      return this.questions;
    }
    const url = `${environment.cdrBase}/v${environment.apiVersion}/questions`;

    return this.http
      .get(url)
      .pipe(
        switchMap((questionsData: any) => {
          if (!!questionsData && !!questionsData.data && questionsData.data.length > 0) {
            const questions = this.fillQuestions(questionsData.data, 'remote');
            return of(questions);
          }
          return of({
            errorCode: 'E301',
            errorMessage: 'remote getQuestions Service failed',
            errorOriginal: 'No questionsData or no data in questionsData',
          });
        }),
        catchError((err) => {
          return of({
            errorCode: 'E301',
            errorMessage: 'remote getQuestions Service failed',
            errorOriginal: err,
          });
        })
      )
      .toPromise();
  }

  async setQuestions(questions: Array<Question>, student: Student): Promise<Array<Question>> {
    this.questions = questions;
    return questions;
  }

  async setQuestion(question: Question, student: Student): Promise<Question> {
    if (!!this.questions && this.questions.find((elt) => elt.id === question.id)) {
      this.questions.splice(
        this.questions.findIndex((elt) => elt.id === question.id),
        1,
        question
      );
      return this.questions.find((elt) => elt.id === question.id);
    }
    this.questions.push(question);
    return question;
  }

  // --- End inlined from RemoteQuestions ---

  async syncQuestions(student: Student, lastUpdate: string, questions: Question[]): Promise<Array<Question>> {
    let oldQuestions = [];
    if (!!student && !!lastUpdate && !!questions) {
      oldQuestions = questions;
      if (oldQuestions.length > 0) {
        let newQuestions = [];
        const resFetchQuestions = await this.fetchQuestions(lastUpdate);
        if (!!resFetchQuestions && !!resFetchQuestions.errorCode && resFetchQuestions.errorCode === 'E301') {
          // service failed
        } else {
          newQuestions = resFetchQuestions;
        }
        if (!!newQuestions && newQuestions.length > 0) {
          for (const question of newQuestions) {
            const newQuestionId = !!question.id ? question.id : null;
            if (!!newQuestionId) {
              let newQuestion = null;
              const resFetchQuestion = question;
              if (!!resFetchQuestion && !!resFetchQuestion.errorCode && resFetchQuestion.errorCode === 'E301') {
                // service failed
              } else {
                newQuestion = resFetchQuestion;
              }
              if (!!newQuestion && !!newQuestion.id) {
                let oldQuestion = null;
                const resGetQuestion = await this.getQuestion(newQuestion.id, student, true);
                if (!!resGetQuestion && !!resGetQuestion.errorCode && resGetQuestion.errorCode === 'E301') {
                  // service failed
                } else {
                  oldQuestion = resGetQuestion;
                }
                if (!!oldQuestion) {
                  newQuestion.downloadState = !!oldQuestion.downloadState ? oldQuestion.downloadState : false;
                  newQuestion = this.fillAssets(newQuestion, oldQuestion);
                  await this.setQuestion(newQuestion, student);
                  const oldQuestionIndex = oldQuestions.findIndex((elt) => elt.id === newQuestion.id);
                  if (oldQuestionIndex !== -1) {
                    oldQuestions.splice(oldQuestionIndex, 1, newQuestion);
                  }
                }
              }
            }
          }
          return await this.setQuestions(oldQuestions, student);
        }
      }
    }
    return oldQuestions;
  }

  fillAssets(newQuestion: Question, oldQuestion: Question): Question {
    if (!!newQuestion && !!oldQuestion) {
      newQuestion.off_answer_audio = !!oldQuestion.off_answer_audio ? oldQuestion.off_answer_audio : null;
      newQuestion.off_answer_img1 = !!oldQuestion.off_answer_img1 ? oldQuestion.off_answer_img1 : null;
      newQuestion.off_answer_img2 = !!oldQuestion.off_answer_img2 ? oldQuestion.off_answer_img2 : null;
      newQuestion.off_question_audio = !!oldQuestion.off_question_audio ? oldQuestion.off_question_audio : null;
      newQuestion.off_question_img1 = !!oldQuestion.off_question_img1 ? oldQuestion.off_question_img1 : null;
      newQuestion.off_question_img2 = !!oldQuestion.off_question_img2 ? oldQuestion.off_question_img2 : null;
      newQuestion.off_question_video = !!oldQuestion.off_question_video ? oldQuestion.off_question_video : null;
    }
    return newQuestion;
  }

  async saveQuestionsIndividualy(questions: Question[], student: Student): Promise<Question[]> {
    const retQuestions: Question[] = [];
    if (!!student && !!questions && questions.length > 0) {
      for (const question of questions) {
        const questionData = await this.setQuestion(question, student);
        if (!!questionData) {
          retQuestions.push(questionData);
        }
      }
    }
    return retQuestions;
  }

  fetchQuestionsBySerie(serieId: string): Observable<any> {
    if (!!serieId) {
      const url = `${environment.cdrBase}/v${environment.apiOptiVersion}/series/${serieId}?with_questions=true`;

      return this.http.get(url).pipe(
        switchMap((seriesData: any) => {
          if (!!seriesData && !!seriesData.data) {
            const attributes = !!seriesData.data.attributes ? seriesData.data.attributes : null;
            if (!!attributes) {
              const data = !!attributes.questions ? attributes.questions : null;
              const questionsData = !!data && !!data.data ? data.data : null;
              if (!!questionsData && questionsData.length > 0) {
                return of(this.fillQuestions(questionsData, 'remote'));
              }
            }
          }
          return of({
            errorCode: 'E301',
            errorMessage: 'fetchQuestionsBySerie Service failed',
            errorOriginal: 'No seriesData or no data in seriesData',
          });
        }),
        catchError((err) => {
          return of({
            errorCode: 'E301',
            errorMessage: 'fetchQuestionsBySerie Service failed',
            errorOriginal: err,
          });
        })
      );
    }
    return of([]);
  }

  async fetchQuestion(questionId: string): Promise<any> {
    if (!!questionId) {
      const url = `${environment.cdrBase}/v${environment.apiVersion}/questions/${questionId}`;

      return this.http
        .get(url)
        .pipe(
          switchMap(async (questionData: any) => {
            if (!!questionData && !!questionData.data) {
              return this.fillQuestion(questionData.data);
            }
            return of({
              errorCode: 'E301',
              errorMessage: 'fetchQuestion Service failed',
              errorOriginal: 'No questionData or no data in questionData',
            });
          }),
          catchError((err) => {
            return of({
              errorCode: 'E301',
              errorMessage: 'fetchQuestion Service failed',
              errorOriginal: err,
            });
          })
        )
        .toPromise();
    }
    return null;
  }

  async fetchQuestions(updatedAt: string): Promise<any> {
    let query = '';

    if (!!updatedAt) {
      query = `?updated_at=${updatedAt}`;
      query = query.replace('+', '%2B');
      const url = `${environment.cdrBase}/v${environment.apiVersion}/questions${query}`;

      return this.http
        .get(url)
        .pipe(
          switchMap(async (questionsData: any) => {
            if (!!questionsData && !!questionsData.data && questionsData.data.length > 0) {
              const questions = this.fillQuestions(questionsData.data, 'remote');
              return questions;
            }
            return [];
          }),
          catchError(async (err) => {
            return {
              errorCode: 'E301',
              errorMessage: 'fetchQuestions Service failed',
              errorOriginal: err,
            };
          })
        )
        .toPromise();
    }
    return [];
  }

  fetchCrashTestQuestions(): Observable<any> {
    const url = `${environment.cdrBase}/v3/cdr/sudden_death/random_questions`;
    return this.http.get(url).pipe(
      switchMap((questionsData: any) => {
        if (!!questionsData?.data) {
          return of(this.fillQuestions(questionsData.data, 'remote'));
        }
        return of({
          errorCode: 'E301',
          errorMessage: 'fetchCrashTestQuestions Service failed',
          errorOriginal: 'No questionsData or no data in questionsData',
        });
      }),
      catchError((err) => {
        return of({
          errorCode: 'E301',
          errorMessage: 'fetchCrashTestQuestions Service failed',
          errorOriginal: err,
        });
      })
    );
  }

  public get questionsToSync(): Array<Question> {
    return this._questionsToSync;
  }
  public set questionsToSync(value: Array<Question>) {
    this._questionsToSync = value;
  }
}
